import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import Tree from 'react-d3-tree';
import { Box, IconButton, Chip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

export default function ReferralTree() {
  const { id } = useParams();
  const [tree, setTree] = useState(null);
  const [dimensions, setDimensions] = useState(null);
  const containerRef = React.useRef(null);
  
  // Fetch tree data
  useEffect(() => { 
    api.get(`/referral-tree/${id}`).then(r => setTree(r.data)); 
  }, [id]);
  
  // Set up dimensions measurement
  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      });
      
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);
  
  if (!tree) return 'Loading...';
  
  /* Convert to react-d3-tree format */
  const mapNode = (n) => ({
    name: n.username || n.id,
    completed: !!n.completed,
    children: Array.isArray(n.children) && n.children.length > 0 ? n.children.map(mapNode) : [],
  });

  const data = useMemo(() => mapNode(tree), [tree]);

  const renderCustomNodeElement = ({ nodeDatum }) => {
    const isCompleted = !!nodeDatum.completed;
    return (
      <g>
        <rect rx={8} ry={8} width={160} height={56} x={-80} y={-28} fill="#fff" stroke={isCompleted ? '#4caf50' : '#e0e0e0'} strokeWidth={2} filter="url(#shadow)" />
        <text textAnchor="middle" y={-4} style={{ fontWeight: 700, fill: '#424242' }}>
          {nodeDatum.name}
        </text>
        <foreignObject x={-30} y={8} width={60} height={24}>
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', justifyContent: 'center' }}>
            <span style={{
              fontSize: 12,
              padding: '2px 8px',
              borderRadius: 12,
              background: isCompleted ? 'rgba(76,175,80,0.1)' : 'rgba(0,0,0,0.05)',
              color: isCompleted ? '#2e7d32' : '#616161',
              border: `1px solid ${isCompleted ? '#a5d6a7' : '#e0e0e0'}`,
            }}>
              {isCompleted ? 'Completed' : 'Not completed'}
            </span>
          </div>
        </foreignObject>
      </g>
    );
  };
  
  const [zoom, setZoom] = useState(0.8);
  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.1, 2));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.2));
  const handleReset = () => setZoom(0.8);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: 'calc(100vh - 100px)', overflow: 'hidden', position: 'relative', bgcolor: 'background.default', borderRadius: 2, p: 1 }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}>
        <IconButton size="small" onClick={handleZoomIn}><ZoomInIcon /></IconButton>
        <IconButton size="small" onClick={handleZoomOut}><ZoomOutIcon /></IconButton>
        <IconButton size="small" onClick={handleReset}><CenterFocusStrongIcon /></IconButton>
      </Box>
      <Tree
        data={data}
        orientation="vertical"
        translate={{ x: dimensions?.width ? dimensions.width / 2 : 500, y: 70 }}
        separation={{ siblings: 2, nonSiblings: 2.5 }}
        pathFunc="straight"
        zoom={zoom}
        renderCustomNodeElement={renderCustomNodeElement}
      />
    </Box>
  );
} 